// Prepaid wallet view-model types and display helpers (no data fetching).

/** Card top-up options (same shape in GET /api/wallet and GET /api/public/config). */
export interface CardTopUps {
  enabled: boolean;
  currency: string;
  minimum: number;
  maximum: number;
  /** One-click amounts set by the admin (TOPUP_PRESETS), already within min/max. */
  presets: number[];
}

export interface WalletSummary {
  balance: number;
  currency: string;
  lowBalanceThreshold: number;
  lowBalance: boolean;
  monthSpend: number;
  cardTopUps: CardTopUps;
  /** Set for agency client accounts: who manages (and prices) this account. */
  agency: ManagedBy | null;
}

/** The agency that manages a client account (GET /api/wallet → agency). */
export interface ManagedBy {
  name: string;
  /** The agency's plan, whose rates the client is charged at. */
  planDisplayName: string | null;
  status: "active" | "suspended";
}

export const TX_TYPES = [
  "usage",
  "purchase",
  "coupon",
  "refund",
  "bonus",
  "adjustment",
  "subscription",
  "agency_earning",
  "transfer_in",
  "transfer_out",
] as const;
export type TxType = (typeof TX_TYPES)[number];
export type TxFilter = "all" | TxType;

export interface WalletTransaction {
  id: string;
  type: TxType | string;
  /** Signed: credits are positive, charges negative. */
  amount: number;
  balanceAfter: number;
  description: string | null;
  referenceType: string | null;
  createdAt: string;
}

export const TX_TYPE_LABELS: Record<TxType, string> = {
  usage: "Usage",
  purchase: "Card top-up",
  coupon: "Coupon",
  refund: "Refund",
  bonus: "Bonus",
  adjustment: "Adjustment",
  subscription: "Plan fee",
  agency_earning: "Client earnings",
  transfer_in: "Transfer in",
  transfer_out: "Transfer out",
};

export const txTypeLabel = (type: string) =>
  TX_TYPE_LABELS[type as TxType] ?? type.charAt(0).toUpperCase() + type.slice(1);

export const CARD_TOP_UPS_UNAVAILABLE = "Card top-ups are coming soon — redeem a coupon code to add credit.";

/**
 * USD amount with cents, widening to up to 6 decimals for sub-cent values
 * (per-request charges are often fractions of a cent).
 */
export function formatCredit(n: number, currency = "USD", { signed = false } = {}) {
  const v = Number.isFinite(n) ? n : 0;
  const abs = Math.abs(v);
  const digits = abs > 0 && abs < 0.01 ? 6 : abs > 0 && abs < 1 ? 4 : 2;
  const text = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: digits,
  }).format(abs);
  if (v < 0) return `-${text}`;
  return signed && v > 0 ? `+${text}` : text;
}

/** Normalise a typed coupon code the way the server compares it. */
export const normalizeCouponInput = (raw: string) => raw.toUpperCase().replace(/\s+/g, "");

/** True when the wallet needs attention (below the alert threshold or empty). */
export const needsCredit = (w: Pick<WalletSummary, "balance" | "lowBalance"> | null | undefined) =>
  !!w && (w.lowBalance || w.balance <= 0);

/** The account's plan subscription (GET /api/wallet/plans → current). */
export interface WalletPlanState {
  /** Plan name, e.g. "payg" (Developer), "pro". */
  plan: string;
  status: string | null;
  currentPeriodEnd: string | null;
  cancelAtPeriodEnd: boolean;
  /** Plan that takes over at currentPeriodEnd (scheduled downgrade), or null. */
  pendingPlan: string | null;
}

/** What the gateway saved the account over a period (GET /api/wallet/savings). */
export interface WalletSavings {
  cacheHits: number;
  cacheSavings: number;
  byokSavings: number;
  /** null when the server can't determine it. */
  failoverRescues: number | null;
  days: number;
}
